import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getLibraryBooks,
  createLibraryBook,
  updateLibraryBook,
  deleteLibraryBook,
  getLibraryBookById,
} from '../services/libraryBookService';
import Notification from '../components/Common/Notification';

// Create and export the context
export const LibraryBookContext = createContext();

// Context Provider component
export const LibraryBookProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [libraryBooks, setLibraryBooks] = useState([]);
  const [selectedLibraryBook, setSelectedLibraryBook] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  // Function to fetch authentication headers
  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  // Function to show notifications
  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  // Function to close notifications
  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  // Function to fetch all library books
  const fetchAllLibraryBooks = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getLibraryBooks(authHeaders);
      setLibraryBooks(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching library books.';
      showNotification(errorMessage, true);
    }
  };

  // Function to fetch a library book by ID
  const fetchLibraryBookById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getLibraryBookById(id, authHeaders);
      setSelectedLibraryBook(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching library book with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  // Function to add a new library book
  const addLibraryBook = async (libraryBookData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newLibraryBook = await createLibraryBook(libraryBookData, authHeaders);
      setLibraryBooks(prevBooks => [...prevBooks, newLibraryBook]);
      showNotification('Library book added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding library book.';
      showNotification(errorMessage, true);
    }
  };

  // Function to edit an existing library book
  const editLibraryBook = async (id, libraryBookData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedLibraryBook = await updateLibraryBook(id, libraryBookData, authHeaders);
      setLibraryBooks(prevBooks =>
        prevBooks.map(book => (book.id === id ? updatedLibraryBook : book))
      );
      showNotification('Library book updated successfully!', false);
      return updatedLibraryBook;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating library book.';
      showNotification(errorMessage, true);
      throw error;
    }
  };

  // Function to remove a library book
  const removeLibraryBook = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteLibraryBook(id, authHeaders);
      setLibraryBooks(prevBooks => prevBooks.filter(book => book.id !== id));
      showNotification('Library book removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing library book.';
      showNotification(errorMessage, true);
    }
  };


  // useEffect(() => {
  //   fetchAllLibraryBooks();
  // }, []);

  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  return (
    <LibraryBookContext.Provider
      value={{
        libraryBooks,
        selectedLibraryBook,
        fetchAllLibraryBooks,
        fetchLibraryBookById,
        addLibraryBook,
        editLibraryBook,
        removeLibraryBook,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </LibraryBookContext.Provider>
  );
};

// Custom hook to use the LibraryBookContext
export const useLibraryBook = () => useContext(LibraryBookContext);
